import { FC, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import theme from '@styles/theme';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { BrowserRouter, Route, useLocation } from 'react-router-dom';
import VisitsRoutes from '@modules/visits/routes';
import { GlobalStyles } from '@styles/global';
import { PrismicProvider, PrismicToolbar } from '@prismicio/react';
import { defaultLinkResolver } from '@core/prismic/resolver';
import PrismicInternalLink from '@core/prismic/components/PrismicInternalLink';
import ResolverRoutes from '@modules/resolver/routes';
import { Routes } from '@core/router';
import CookieConsent from '@innovantic/cookie-consent';
import AnalyticsTracker from '@shared/modules/analytics/components/AnalyticsTracker';
import { cookieConsentEnglishText } from '@shared/modules/analytics/model';
import SentryProvider from '@shared/modules/sentry/components/SentryProvider';
import { useCurrentLanguage } from '@shared/modules/translation/hooks';
import AccountTypeModal, {
  AccountTypeModalProvider,
  useAccountTypeModalContext,
} from '@shared/modules/account-type-modal';
import { getConfigValue } from '@shared/utils/config';
import * as O from 'fp-ts/Option';

const ScrollRestoration: FC = ({ children }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return <>{children}</>;
};

const AppContent: FC = () => {
  const language = useCurrentLanguage();
  const { isModalOpen, closeModal } = useAccountTypeModalContext();

  return (
    <>
      <Helmet>
        <html lang={language} />
      </Helmet>

      <CookieConsent hideCustomizeButton text={language === 'en' ? cookieConsentEnglishText : undefined}>
        <BrowserRouter>
          <AnalyticsTracker />
          <PrismicProvider linkResolver={defaultLinkResolver} internalLinkComponent={PrismicInternalLink}>
            <ScrollRestoration>
              <Routes>
                <Route path=":id/*" element={<VisitsRoutes />} />
                <Route path="resolver/*" element={<ResolverRoutes />} />
              </Routes>
            </ScrollRestoration>
          </PrismicProvider>
        </BrowserRouter>
      </CookieConsent>

      <PrismicToolbar repositoryName={O.getOrElse(() => 'vegetag')(getConfigValue('VITE_PRISMIC_REPOSITORY'))} />

      <AccountTypeModal isOpen={isModalOpen} onClose={closeModal} />
    </>
  );
};

const App: FC = () => {
  return (
    <SentryProvider>
      <HelmetProvider>
        <ThemeProvider theme={theme}>
          <GlobalStyles />
          <AccountTypeModalProvider>
            <AppContent />
          </AccountTypeModalProvider>
        </ThemeProvider>
      </HelmetProvider>
    </SentryProvider>
  );
};

export default App;
